<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import * as turf from '@turf/turf';

mapboxgl.accessToken =
    'pk.eyJ1IjoiYWFyY2lsYTE4IiwiYSI6ImNsM2V6YWdscjBuZDMzY3A3M2FtMDl6YWgifQ.adh5HQ_qhIy3cjG1a-pSAQ';

const props = defineProps({
    center: {
        type: Array,
        required: true,
    },
    farmArea: {
        type: Array,
        default: () => [],
    },
    mode: {
        type: String,
        required: true,
    },
    treePoints: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(['selectedArea', 'treePointAdded']);
const mapContainer = ref(null);
const mapReady = ref(false);
const drawing = ref(false);
const areaSquareMeters = ref(0);
const gpsLoading = ref(false);
const gpsError = ref('');

let map = null;
let draw = null;
let geocoder = null;
let treeMarkers = [];
let touchStartPoint = null;
let touchStartedAt = 0;
let ignoreClickUntil = 0;

const MAX_TAP_DISTANCE = 12;
const MAX_TAP_DURATION = 750;
const SYNTHETIC_CLICK_DELAY = 800;

const hasArea = computed(() => areaSquareMeters.value > 0);
const instruction = computed(() => {
    if (props.mode === 'trees') {
        return 'Toca o haz clic sobre el mapa para agregar cada punto. Después completa su información.';
    }

    if (drawing.value) {
        return 'Marca cada esquina del cultivo y vuelve al primer punto para cerrar el polígono.';
    }

    if (hasArea.value) {
        return 'Área lista. Puedes redibujarla antes de guardar.';
    }

    return 'Selecciona “Dibujar área” y marca el contorno del cultivo.';
});

const formattedArea = computed(() => {
    if (!areaSquareMeters.value) {
        return '';
    }

    return `${(areaSquareMeters.value / 10000).toLocaleString('es-CO', { maximumFractionDigits: 4 })} ha · ${areaSquareMeters.value.toLocaleString('es-CO', { maximumFractionDigits: 2 })} m²`;
});

function normalizedFarmArea() {
    if (props.farmArea.length < 3) {
        return [];
    }

    const coordinates = props.farmArea.map(point => [Number(point[0]), Number(point[1])]);
    const first = coordinates[0];
    const last = coordinates[coordinates.length - 1];

    if (first[0] !== last[0] || first[1] !== last[1]) {
        coordinates.push([...first]);
    }

    return coordinates;
}

function addFarmBoundary() {
    const coordinates = normalizedFarmArea();
    if (coordinates.length < 4) {
        return;
    }

    const farmPolygon = turf.polygon([coordinates]);
    map.addSource('farm-boundary', {
        type: 'geojson',
        data: farmPolygon,
    });
    map.addLayer({
        id: 'farm-boundary-fill',
        type: 'fill',
        source: 'farm-boundary',
        paint: {
            'fill-color': '#F3E022',
            'fill-opacity': 0.12,
        },
    });
    map.addLayer({
        id: 'farm-boundary-line',
        type: 'line',
        source: 'farm-boundary',
        paint: {
            'line-color': '#F3E022',
            'line-width': 3,
        },
    });
    map.fitBounds(turf.bbox(farmPolygon), { padding: 55, maxZoom: 17, duration: 0 });
}

function clearTreeMarkers() {
    treeMarkers.forEach(marker => marker.remove());
    treeMarkers = [];
}

function syncTreeMarkers() {
    if (!mapReady.value || !map) {
        return;
    }

    clearTreeMarkers();
    props.treePoints.forEach((tree, index) => {
        const markerElement = document.createElement('div');
        markerElement.className = 'harvest-tree-marker';
        markerElement.textContent = String(index + 1);
        const marker = new mapboxgl.Marker({ element: markerElement })
            .setLngLat([Number(tree.lon), Number(tree.lat)])
            .addTo(map);
        treeMarkers.push(marker);
    });
}

function addTreePoint(lngLat) {
    if (props.mode !== 'trees' || !lngLat) {
        return;
    }

    emit('treePointAdded', [lngLat.lng, lngLat.lat]);
}

function touchPoint(event, changed = false) {
    const originalEvent = event.originalEvent;
    const touches = changed ? originalEvent?.changedTouches : originalEvent?.touches;
    const touch = touches?.[0];

    if (touch && map) {
        const bounds = map.getCanvasContainer().getBoundingClientRect();
        return {
            x: touch.clientX - bounds.left,
            y: touch.clientY - bounds.top,
        };
    }

    const point = event.points?.[0] || event.point;
    return point ? { x: point.x, y: point.y } : null;
}

function resetTouch() {
    touchStartPoint = null;
    touchStartedAt = 0;
}

function handleTreeTouchStart(event) {
    if (props.mode !== 'trees' || event.originalEvent?.touches?.length !== 1) {
        resetTouch();
        return;
    }

    touchStartPoint = touchPoint(event);
    touchStartedAt = Date.now();
}

function handleTreeTouchEnd(event) {
    if (props.mode !== 'trees' || !touchStartPoint || !touchStartedAt) {
        resetTouch();
        return;
    }

    const endPoint = touchPoint(event, true);
    const duration = Date.now() - touchStartedAt;
    const distance = endPoint
        ? Math.hypot(endPoint.x - touchStartPoint.x, endPoint.y - touchStartPoint.y)
        : Number.POSITIVE_INFINITY;

    if (distance <= MAX_TAP_DISTANCE && duration <= MAX_TAP_DURATION && endPoint) {
        addTreePoint(map.unproject([endPoint.x, endPoint.y]));
        ignoreClickUntil = Date.now() + SYNTHETIC_CLICK_DELAY;
    }

    resetTouch();
}

function handleTreeClick(event) {
    if (Date.now() < ignoreClickUntil) {
        return;
    }

    addTreePoint(event.lngLat);
}

function clearArea() {
    if (draw) {
        draw.deleteAll();
        draw.changeMode('simple_select');
    }
    drawing.value = false;
    areaSquareMeters.value = 0;
    emit('selectedArea', { locations: [], area: 0, center: null });
}

function startAreaDrawing() {
    if (!draw) {
        return;
    }

    clearArea();
    drawing.value = true;
    draw.changeMode('draw_polygon');
}

function updateArea() {
    const data = draw.getAll();
    if (!data.features.length) {
        areaSquareMeters.value = 0;
        emit('selectedArea', { locations: [], area: 0, center: null });
        return;
    }

    const feature = data.features[0];
    const area = Math.round(turf.area(feature) * 100) / 100;
    const center = turf.centroid(feature).geometry.coordinates;
    areaSquareMeters.value = area;
    drawing.value = false;
    emit('selectedArea', {
        locations: feature.geometry.coordinates[0],
        area,
        center,
    });
    map.fitBounds(turf.bbox(feature), { padding: 60, maxZoom: 19, duration: 600 });
}

function useGps() {
    gpsError.value = '';
    if (!navigator.geolocation) {
        gpsError.value = 'Este dispositivo no ofrece ubicación GPS.';
        return;
    }

    gpsLoading.value = true;
    navigator.geolocation.getCurrentPosition(
        position => {
            gpsLoading.value = false;
            map.flyTo({
                center: [position.coords.longitude, position.coords.latitude],
                zoom: 18,
                essential: true,
            });
        },
        error => {
            gpsLoading.value = false;
            gpsError.value = error.code === 1
                ? 'Permite el acceso a tu ubicación para usar el GPS.'
                : 'No pudimos obtener tu ubicación. Intenta nuevamente.';
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 }
    );
}

watch(() => props.treePoints, syncTreeMarkers, { deep: true });
watch(() => props.mode, () => {
    gpsError.value = '';
    if (props.mode === 'trees') {
        clearArea();
    } else {
        clearTreeMarkers();
    }
});

onMounted(() => {
    map = new mapboxgl.Map({
        container: mapContainer.value,
        style: 'mapbox://styles/mapbox/satellite-streets-v11',
        center: props.center,
        zoom: 16,
        cooperativeGestures: false,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.addControl(new mapboxgl.FullscreenControl(), 'top-right');

    geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl,
        marker: false,
        placeholder: 'Buscar lugar',
    });
    map.addControl(geocoder, 'top-left');

    map.on('load', () => {
        draw = new MapboxDraw({ displayControlsDefault: false, controls: {} });
        map.addControl(draw);
        mapReady.value = true;
        addFarmBoundary();
        syncTreeMarkers();

        map.on('draw.create', updateArea);
        map.on('draw.update', updateArea);
        map.on('draw.delete', updateArea);
    });

    map.on('touchstart', handleTreeTouchStart);
    map.on('touchend', handleTreeTouchEnd);
    map.on('click', handleTreeClick);
});

onBeforeUnmount(() => {
    clearTreeMarkers();
    if (map) {
        map.remove();
    }
});
</script>

<template>
    <section class="space-y-3">
        <div class="flex flex-wrap items-center gap-2">
            <button
                v-if="mode === 'area'"
                type="button"
                class="harvest-map-action"
                :class="{ 'harvest-map-action-active': drawing }"
                :disabled="!mapReady"
                @click="startAreaDrawing"
            >
                <span class="material-symbols-outlined">polyline</span>
                {{ hasArea ? 'Redibujar área' : 'Dibujar área' }}
            </button>
            <button
                type="button"
                class="harvest-map-action harvest-map-gps md:hidden"
                :disabled="gpsLoading"
                @click="useGps"
            >
                <span class="material-symbols-outlined">my_location</span>
                {{ gpsLoading ? 'Buscando GPS…' : 'Centrar con mi GPS' }}
            </button>
            <span class="flex items-center gap-2 text-sm text-gray-700">
                <span class="inline-block w-6 h-1 bg-enriqueto_yellow"></span>
                Límite de la finca
            </span>
        </div>

        <p class="text-sm text-enriqueto_green">{{ instruction }}</p>
        <p v-if="gpsError" class="p-3 text-sm text-red-700 bg-red-50 rounded-xl" role="alert">
            {{ gpsError }}
        </p>

        <div
            ref="mapContainer"
            class="w-full overflow-hidden border-2 harvest-creation-map rounded-2xl border-light_green"
        ></div>

        <div v-if="mode === 'area' && hasArea" class="flex items-center justify-between gap-3 p-3 bg-light_yellow rounded-xl">
            <div>
                <p class="text-sm font-bold text-enriqueto_green">Área calculada</p>
                <p class="text-sm text-enriqueto_green">{{ formattedArea }}</p>
            </div>
            <button type="button" class="text-sm text-red-700 underline" @click="startAreaDrawing">
                Volver a dibujar
            </button>
        </div>

        <div v-if="mode === 'trees'" class="p-3 text-sm bg-light_yellow rounded-xl text-enriqueto_green">
            {{ treePoints.length }} {{ treePoints.length === 1 ? 'punto marcado' : 'puntos marcados' }}
        </div>
    </section>
</template>

<style scoped>
.harvest-creation-map {
    height: 68vh;
    min-height: 640px;
}

.harvest-map-action {
    align-items: center;
    border: 1px solid #03662E;
    border-radius: 0.75rem;
    color: #03662E;
    display: flex;
    gap: 0.35rem;
    justify-content: center;
    padding: 0.65rem 0.9rem;
}

.harvest-map-action:hover,
.harvest-map-action-active {
    background: #03662E;
    color: white;
}

.harvest-map-action:disabled {
    cursor: not-allowed;
    opacity: 0.45;
}

.harvest-map-gps {
    background: #F3E022;
    border-color: #F3E022;
    color: #03662E;
    font-weight: 700;
}

:deep(.harvest-tree-marker) {
    align-items: center;
    background: #03662E;
    border: 3px solid white;
    border-radius: 9999px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.35);
    color: white;
    display: flex;
    font-size: 0.8rem;
    font-weight: 700;
    height: 32px;
    justify-content: center;
    width: 32px;
}

@media (max-width: 640px) {
    .harvest-creation-map {
        height: 62vh;
        min-height: 500px;
    }
}

@media (min-width: 768px) {
    .harvest-map-gps {
        display: none !important;
    }
}
</style>
